import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import api from '../../lib/api';

/**
 * Master Brand toggle (v4.46). Reads/writes the org-level flag directly —
 * resolution itself happens server-side at read time, so no card is ever
 * written to and a later portal-logo change propagates on its own.
 */
export const MasterBrandToggle = ({ orgId, hasPortalLogo, disabled = false }) => {
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(!!orgId);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!orgId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        api.get(`/organizations/${orgId}/master-brand`)
            .then((res) => setEnabled(!!res.data?.master_brand_enabled))
            .catch(() => setEnabled(false))
            .finally(() => setLoading(false));
    }, [orgId]);

    const toggle = async (next) => {
        setSaving(true);
        setEnabled(next);
        try {
            await api.put(`/organizations/${orgId}/master-brand`, { master_brand_enabled: next });
            toast.success(next
                ? t('masterBrand.on', 'Master Brand is on')
                : t('masterBrand.off', 'Master Brand is off'));
        } catch (error) {
            setEnabled(!next);
            toast.error(error.response?.data?.detail || t('masterBrand.failed', 'Could not save Master Brand'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-lg border p-4 mt-4" data-testid="master-brand-section">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <Label className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        {t('masterBrand.title', 'Master Brand')}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('masterBrand.explainer', 'Applies automatically to Review Shield, Global Review, Hotel Ops, Vouchers/Gift Cards, and the portal icon unless overridden per location.')}
                    </p>
                    {!orgId ? (
                        <p className="text-xs text-amber-600 mt-1.5" data-testid="master-brand-save-first">
                            {t('masterBrand.saveFirst', 'Save the client first, then reopen to set this.')}
                        </p>
                    ) : null}
                    {orgId && enabled && !hasPortalLogo ? (
                        <p className="text-xs text-amber-600 mt-1.5" data-testid="master-brand-no-logo">
                            {t('masterBrand.noLogo', 'Upload a portal logo above — there is nothing to apply yet.')}
                        </p>
                    ) : null}
                </div>
                {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : (
                    <Switch
                        checked={enabled}
                        disabled={disabled || saving || !orgId}
                        onCheckedChange={toggle}
                        data-testid="master-brand-switch"
                    />
                )}
            </div>
        </div>
    );
};

export default MasterBrandToggle;
